import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  adoptListingRemakeResult,
  cancelListingRemake,
  createAuditShare,
  deactivateAuditShare,
  deleteListingAudit,
  getAuditShare,
  getListingAuditReport,
  getListingAuditStatus,
  getListingRemakeFull,
  getListingRemakeSlots,
  getListingRemakeStatus,
  getListingRemakeVersions,
  listAuditVersions,
  listListingAudits,
  listMarketplaceProfiles,
  reauditListingAudit,
  startBatchAudit,
  startListingAudit,
  startListingRemake,
} from '@/lib/api/listing-builder'

const _stopWhenTerminal = (status?: string) =>
  status === 'completed' || status === 'failed' ? false : 3000

export function useMarketplaceProfiles() {
  return useQuery({
    queryKey: ['marketplace-profiles'],
    queryFn: listMarketplaceProfiles,
    staleTime: 300_000,
  })
}

export function useStartListingAudit() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: startListingAudit,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['listing-builder'] }),
  })
}

export function useListingAuditStatus(jobId: string | null) {
  return useQuery({
    queryKey: ['listing-builder', 'audit-status', jobId],
    queryFn: () => getListingAuditStatus(jobId!),
    enabled: !!jobId,
    refetchInterval: (query) =>
      query.state.error ? false : _stopWhenTerminal(query.state.data?.status),
    retry: 1,
  })
}

export function useListingAuditReport(jobId: string | null) {
  return useQuery({
    queryKey: ['listing-builder', 'audit-report', jobId],
    queryFn: () => getListingAuditReport(jobId!),
    enabled: !!jobId,
    staleTime: 60_000,
  })
}

export function useStartListingRemake(jobId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { marketplace_code: string; sales_channel_id?: string; fields?: string[] }) =>
      startListingRemake(jobId, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['listing-builder', 'remake-status', jobId] }),
  })
}

export function useListingRemakeStatus(jobId: string | null) {
  return useQuery({
    queryKey: ['listing-builder', 'remake-status', jobId],
    queryFn: () => getListingRemakeStatus(jobId!),
    enabled: !!jobId,
    refetchInterval: (query) =>
      query.state.error ? false : _stopWhenTerminal(query.state.data?.status),
  })
}

const _ACTIVE_AUDIT = new Set(['pending', 'queued', 'running', 'scraping', 'analyzing'])

export function useListingAudits(params: { limit?: number; offset?: number; search?: string }) {
  return useQuery({
    queryKey: ['listing-builder', 'audits', params],
    queryFn: () => listListingAudits(params),
    // Solange ein Audit in Arbeit ist, die Liste pollen, damit der Status
    // (in Arbeit -> fertig) ohne Reload aktualisiert wird.
    refetchInterval: (query) =>
      (query.state.data?.items ?? []).some((i) => _ACTIVE_AUDIT.has(i.status)) ? 4000 : false,
  })
}

export function useDeleteListingAudit() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: deleteListingAudit,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['listing-builder', 'audits'] }),
  })
}

export function useListingRemakeFull(remakeId: string | null) {
  return useQuery({
    queryKey: ['listing-builder', 'remake-full', remakeId],
    queryFn: () => getListingRemakeFull(remakeId!),
    enabled: !!remakeId,
  })
}

export function useListingRemakeSlots(remakeId: string | null) {
  return useQuery({
    queryKey: ['listing-builder', 'remake-slots', remakeId],
    queryFn: () => getListingRemakeSlots(remakeId!),
    enabled: !!remakeId,
    refetchInterval: (query) =>
      query.state.data?.slots?.some(
        (s) => s.status === 'pending' || s.status === 'generating',
      )
        ? 3000
        : false,
  })
}

export function useListingRemakeVersions(jobId: string | null) {
  return useQuery({
    queryKey: ['listing-builder', 'remake-versions', jobId],
    queryFn: () => getListingRemakeVersions(jobId!),
    enabled: !!jobId,
  })
}

export function useCancelListingRemake(jobId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (remakeId: string) => cancelListingRemake(remakeId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['listing-builder', 'remake-status', jobId] }),
  })
}

export function useAdoptListingRemake() {
  return useMutation({
    mutationFn: (remakeId: string) => adoptListingRemakeResult(remakeId),
  })
}

export function useListingAuditVersions(jobId: string | null) {
  return useQuery({
    queryKey: ['listing-builder', 'audit-versions', jobId],
    queryFn: () => listAuditVersions(jobId!),
    enabled: !!jobId,
  })
}

export function useStartBatchListingAudit() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: startBatchAudit,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['listing-builder'] }),
  })
}

export function useAuditShare(jobId: string | null) {
  return useQuery({
    queryKey: ['listing-builder', 'audit-share', jobId],
    queryFn: () => getAuditShare(jobId!),
    enabled: !!jobId,
  })
}

export function useCreateAuditShare(jobId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data?: { password?: string }) => createAuditShare(jobId, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['listing-builder', 'audit-share', jobId] }),
  })
}

export function useDeactivateAuditShare(jobId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => deactivateAuditShare(jobId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['listing-builder', 'audit-share', jobId] }),
  })
}

export function useReauditListingAudit() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: reauditListingAudit,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['listing-builder'] }),
  })
}
